import React from 'react'
import { faBookmark, faChevronRight } from '@fortawesome/free-solid-svg-icons'
import { faBookmark as faBookmarkRegular } from '@fortawesome/free-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import styled, { css } from 'styled-components'
import theme, { institutionalColors } from '~/design'
import Link from '~/prix/react/components/link'
import { PulseLoader } from 'react-spinners'
import { NewArticleCard } from '~/prix/react/components/card'
import { Skeleton } from '~/components/skeletonLoading'
import { AnalyticalLibraryTheme } from './analyticalLibraryItems'

const Card = styled(NewArticleCard)`
  position: relative;
  min-height: 100%;
  padding: 0;
  transition: transform 0.1s ease-in-out;

  &:hover {
    transform: scale(1.02);
  }
`

const FixedIconWrapper = styled.div`
  position: absolute;
  top: -12px;
  right: 12px;
  color: ${({ theme }) => theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;

  svg {
    width: 24px;
    height: 24px;
  }
`

const ImageWrapper = styled.div`
  height: 165px;
  flex: 0 0 165px;
  margin: 12px 12px 0 12px;
  border-radius: 5px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`

const Body = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
  gap: 5px;
  padding: 12px;

  h2 {
    margin: 0;
    color: ${({ theme }) => theme.colors.darkGrey};
    font-size: 16px;
    font-weight: 700;
    line-height: 1.15;
    margin-bottom: 5px;
  }

  p {
    margin: 0;
    color: ${institutionalColors.graySebrae45};
    font-size: 14px;
    line-height: 1.15;
  }
`

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 5px;
`

const Tag = styled.span<{ color: string }>`
  padding: 3px 8px;
  border-radius: 999px;
  background: ${({ color }) => `${color}33`};
  border: 1px solid ${({ color }) => color};
  color: ${({ color }) => color};
  font-size: 11px;
  font-weight: 600;
  line-height: 1.2;
`

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 5px;
  padding-top: 8px;
  gap: 8px;
`

const cardLinkStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  color: ${({ theme }) => theme.colors.darkGrey};
  border: 1px solid ${({ theme }) => theme.colors.darkGrey};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
  text-decoration: none;
  transition: all 0.2s ease-in-out;

  svg,
  svg > * {
    color: ${({ theme }) => theme.colors.darkGrey};
    height: 12px;
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};

    svg,
    svg > * {
      color: ${({ theme }) => theme.colors.primary};
    }
  }
`

const CardLink = styled(Link)`
  ${cardLinkStyle}
`

const CardLinkSecondary = styled.button`
  ${cardLinkStyle}
  background: none;
  border: none;
  cursor: pointer;

  svg,
  svg > * {
    height: 16px;
  }
`

export type AnalyticalLibraryCardProperties = {
  title: string
  imageSource: string
  imageAlternativeText: string
  description: string
  destinationPath: string
  redirect?: boolean
  tags?: AnalyticalLibraryTheme[]
  isFixed?: boolean
  isChangingFixedState?: boolean
  onChangeFixedState?: (isFixed: boolean) => void
}

export function AnalyticalLibraryCardSkeleton() {
  return (
    <Card>
      <ImageWrapper>
        <Skeleton style={{ minHeight: 165, width: '100%' }} />
      </ImageWrapper>
      <Body>
        <Tags>
          <Skeleton style={{ minHeight: 20, width: '40%' }} />
          <Skeleton style={{ minHeight: 20, width: '30%' }} />
        </Tags>
        <Skeleton style={{ minHeight: 15, width: '70%' }} />
        <Skeleton style={{ minHeight: 10, width: '100%' }} />
        <Skeleton style={{ minHeight: 10, width: '100%' }} />
        <Skeleton style={{ minHeight: 10, width: '50%' }} />
        <Footer>
          <Skeleton style={{ minHeight: 30, width: '30%' }} />
          <Skeleton style={{ minHeight: 30, width: '30%' }} />
        </Footer>
      </Body>
    </Card>
  )
}

export default function AnalyticalLibraryCard({
  title,
  imageSource,
  imageAlternativeText,
  description,
  destinationPath,
  tags,
  redirect = false,
  isFixed = false,
  isChangingFixedState = false,
  onChangeFixedState,
}: AnalyticalLibraryCardProperties) {
  return (
    <Card>
      {isFixed && (
        <FixedIconWrapper>
          <FontAwesomeIcon icon={faBookmark} />
        </FixedIconWrapper>
      )}
      <ImageWrapper>
        <img src={imageSource} alt={imageAlternativeText} />
      </ImageWrapper>
      <Body>
        <div>
          {tags && tags.length > 0 && (
            <Tags>
              {tags.map(tag => (
                <Tag key={tag.name} color={tag.color}>
                  {tag.name}
                </Tag>
              ))}
            </Tags>
          )}
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
        <Footer>
          {onChangeFixedState && (
            <CardLinkSecondary
              type='button'
              aria-pressed={isFixed}
              aria-label={isFixed ? 'Desafixar relatório ou estudo' : 'Fixar relatório ou estudo'}
              onClick={() => onChangeFixedState(!isFixed)}
              disabled={isChangingFixedState}
            >
              {isChangingFixedState ? (
                <PulseLoader color={theme.colors.darkGrey} size={6} />
              ) : (
                <>
                  {isFixed ? 'desafixar' : 'fixar'}
                  <FontAwesomeIcon icon={isFixed ? faBookmark : faBookmarkRegular} />
                </>
              )}
            </CardLinkSecondary>
          )}
          <CardLink
            href={destinationPath}
            remote={/^https?:\/\//.test(destinationPath)}
            newTab={redirect}
          >
            Acessar
            <FontAwesomeIcon icon={faChevronRight} />
          </CardLink>
        </Footer>
      </Body>
    </Card>
  )
}
